'use client'

import React from 'react'
import Link from 'next/link'
import { cn } from '../../../../../lib/utils'
import { ButtonProps } from '../../../Button'

import s from '../../../../styles/animations.module.css'
import { Icon, iconTypes } from '../../../Icon'
import { Chip, ChipProps } from '../../../Chip'

export type ActionTileProps = {
  isSelected?: boolean
  header?: string
  /** Alias for header - use either header or label */
  label?: string
  description?: string
  statusChips?: ChipProps[]
  id: string
  actions?: ButtonProps[]
  chips?: ChipProps[]
  handleClick?: () => void
  /** Optional left icon name */
  leftIcon?: string
  /** Optional href for navigation - when provided, tile acts as a link */
  href?: string
}

export const ActionTile = (props: ActionTileProps) => {
  const {
    id,
    isSelected,
    header,
    label,
    description,
    actions,
    chips,
    statusChips,
    handleClick,
    leftIcon,
    href,
  } = props

  // Use header or label as the display title
  const displayTitle = header ?? label ?? ''

  const tileContent = (
    <>
      <div className="flex flex-row gap-4 items-center w-full text-sm justify-between">
        <div className="flex flex-row gap-2 items-center">
          {leftIcon && <Icon name={leftIcon as iconTypes} size={16} />}
          <span className="underline decoration-dotted">{displayTitle}</span>
          {statusChips &&
            statusChips.map((statusChip, index) => (
              <Chip key={index} {...statusChip} />
            ))}
        </div>
        <div className="flex gap-1 items-center flex-wrap">
          {actions &&
            actions.map((a) => (
              <button
                key={`action-${id}-${a.label}`}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  a.handleClick?.(e)
                }}
                disabled={a.isDisabled || a.isLoading}
                className={cn(
                  'border rounded-lg px-2 h-6 text-xs flex items-center justify-center text-nowrap flex gap-1 bg-primary text-secondary hover:bg-secondary hover:text-primary',
                )}
              >
                {a.isLoading && (
                  <Icon
                    name={'LoaderCircle'}
                    className="animate-spin"
                    size={12}
                  />
                )}
                {!a.isLoading && a.leftIcon && (
                  <Icon name={a.leftIcon as iconTypes} size={12} />
                )}
                {a.label}
              </button>
            ))}
        </div>
      </div>
      {description && <div className="text-xs">{description}</div>}
      <div className="flex flex-row">
        <div className="flex gap-1 items-center flex-wrap">
          {chips && chips.map((c, index) => <Chip key={index} {...c} />)}
        </div>
      </div>
    </>
  )

  const tileClassName = cn(
    'rounded-lg flex p-2 flex flex-col hover:bg-muted w-full gap-2 cursor-pointer',
    {
      'bg-accent': isSelected,
    },
    s.smallTransitionIn,
  )

  // If href is provided and valid, wrap in Link for navigation
  if (href && typeof href === 'string' && href.trim() !== '') {
    return (
      <Link href={href} className={tileClassName} title={`list-tile-${id}`}>
        {tileContent}
      </Link>
    )
  }

  // Otherwise, use div with onClick handler
  return (
    <div
      className={tileClassName}
      title={`list-tile-${id}`}
      onClick={(e) => {
        // Prevents the click event from firing when clicking on a button
        if ((e.target as HTMLElement).tagName === 'BUTTON') {
          return
        }

        handleClick?.()
      }}
    >
      {tileContent}
    </div>
  )
}
